import { Route, RouteRecord } from 'vue-router'
import USER_TYPES from '@/constants/authorization/user-types'
import { SETUP_WIZARDS } from '@/constants/setup-wizards'
import store from '@/store'
import { PACKAGE_PERMISSIONS } from '@/constants/features'
import { getRoutePermission } from '@/constants/authorization/route-permissions'

export function getRequiredPermissionsFromRoute(to: Route) {
  const permissions: Array<Array<string>> = to.matched.reduce((prev: any, current: RouteRecord) => {
    const { permission, permissions: routePermissions } = current?.meta ?? {}
    const required = routePermissions ?? permission ?? getRoutePermission(current.name)
    if (!required) return prev
    return [...prev, Array.isArray(required) ? required : [required]]
  }, [])
  return permissions
}

export function isSubuserHasPermissions(permissions: Array<Array<string>>, permissionChecker: Function) {
  for (let i = 0; i < permissions.length; i += 1) {
    const items = permissions[i]
    const isFailed = !items.some((permission: string) => permissionChecker(permission))
    if (isFailed) {
      return false
    }
  }
  return true
}

export function getRequiredUserTypesFromRoute(to: Route) {
  const requiredTypes: Array<Array<string>> = to.matched.reduce((prev: any, current: RouteRecord) => {
    const { onlyOwner, onlySuperAdmin } = current?.meta ?? {}

    if (onlySuperAdmin) return [...prev, [USER_TYPES.SUPER_ADMIN]]
    if (onlyOwner) return [...prev, [USER_TYPES.OWNER]]

    return prev
  }, [])
  return requiredTypes
}

export function isUserHasTypes(userTypes: Array<Array<string>>, isUserHasType: Function) {
  for (let i = 0; i < userTypes.length; i += 1) {
    const types = userTypes[i]
    const isFailed = !types.some((type: string) => isUserHasType(type, false))
    if (isFailed) {
      return false
    }
  }
  return true
}

export function getAdminPermissionsFromRoute(to: Route) {
  const permissions: Array<Array<string>> = to.matched.reduce((prev: any, current: RouteRecord) => {
    const { adminPermissions } = current?.meta ?? {}
    return (adminPermissions ? [...prev, Array.isArray(adminPermissions) ? adminPermissions : [adminPermissions]] : prev)
  }, [])
  return permissions
}

export function isSuperAdminHasPermissions(permissions: Array<Array<string>>, permissionChecker: Function) {
  for (let i = 0; i < permissions.length; i += 1) {
    const types = permissions[i]
    const isFailed = !types.some((permission: string) => permissionChecker(permission))
    if (isFailed) {
      return false
    }
  }
  return true
}

export function getPackagePermissionsFromRoute(to: Route) {
  const permissions: Array<Array<string>> = to.matched.reduce((prev: any, current: RouteRecord) => {
    const { packagePermissions } = current?.meta ?? {}
    return (packagePermissions ? [...prev, Array.isArray(packagePermissions) ? packagePermissions : [packagePermissions]] : prev)
  }, [])
  return permissions
}

export function isSubscriptionHasPermissions(permissions: Array<Array<string>>, permissionChecker: Function) {
  for (let i = 0; i < permissions.length; i += 1) {
    const types = permissions[i]
    const isFailed = !types.some((permission: string) => permissionChecker(permission))
    if (isFailed) {
      return false
    }
  }
  return true
}

export function skipWizardByPackagePermission(wizard: string) {
  switch (wizard) {
    // case SETUP_WIZARDS.QUEUE_RESERVATION: return !(store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.RESERVATION) && store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.QUEUE))
    case SETUP_WIZARDS.DEVICES: case SETUP_WIZARDS.DESIGN_TABLET: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.IPAD)
    case SETUP_WIZARDS.DESIGN_QR: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.QR_CUSTOMIZATION)
    case SETUP_WIZARDS.SURVEY: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.FEEDBACK)
    case SETUP_WIZARDS.COUPONS: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.COUPON)
    case SETUP_WIZARDS.DISCOUNTS: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.DISCOUNT)
    case SETUP_WIZARDS.NEWSLETTER: return !store.getters['subscription/canAccessFeature'](PACKAGE_PERMISSIONS.NEWSLETTER)
    default: return false
  }
}

export function skipWizardStepByPackagePermission(step: string) {
  switch (step) {
    case 'queue': case 'reservation': return !store.getters['subscription/canAccessFeature'](step)
    default: return false
  }
}

type NavItem = {
  route?: string;
  permission?: string | string[];
  permissions?: string | string[];
  children?: NavItem[];
  hide?: () => boolean;
}

function flattenNavItems(items: NavItem[] = [], acc: NavItem[] = []): NavItem[] {
  items.forEach(item => {
    if (item.route) acc.push(item)
    if (item.children?.length) flattenNavItems(item.children, acc)
  })
  return acc
}

function getNavItemPermissions(item: NavItem): string[] | null {
  const perms = item.permission ?? item.permissions ?? (item.route ? getRoutePermission(item.route) : null)
  if (!perms) return null
  return Array.isArray(perms) ? perms : [perms]
}

function isNavItemAccessible(
  item: NavItem,
  hasAnySubuserPermission: (permissions: string[]) => boolean,
): boolean {
  if (item.hide?.()) return false

  const list = getNavItemPermissions(item)
  if (!list?.length) return !!item.route

  return hasAnySubuserPermission(list)
}

function findNavGroupForRoute(routeName: string, items: NavItem[] = [], parents: NavItem[] = []): NavItem | null {
  for (const item of items) {
    if (item.route === routeName) {
      for (let i = parents.length - 1; i >= 0; i -= 1) {
        if (parents[i].children?.length) return parents[i]
      }
      return null
    }

    if (item.children?.length) {
      const found = findNavGroupForRoute(routeName, item.children, [...parents, item])
      if (found) return found
    }
  }

  return null
}

/** First sibling route in the same sidebar group the subuser can access. */
export function getFirstAccessibleSiblingRoute(
  routeName: string,
  hasAnySubuserPermission: (permissions: string[]) => boolean,
  navItems: NavItem[] = [],
) {
  const group = findNavGroupForRoute(routeName, navItems)
  if (!group?.children?.length) return null

  for (const child of group.children) {
    if (!isNavItemAccessible(child, hasAnySubuserPermission) || !child.route) continue
    return { name: child.route }
  }

  return null
}

/** First sidebar route the subuser can access (view permission). */
export function getFirstAccessibleSubuserRoute(
  hasAnySubuserPermission: (permissions: string[]) => boolean,
  navItems: NavItem[] = [],
) {
  const routes = flattenNavItems(navItems)

  for (const item of routes) {
    if (isNavItemAccessible(item, hasAnySubuserPermission) && item.route) {
      return { name: item.route }
    }
  }

  return null
}

/** Prefer first allowed sibling route, then first allowed route in the sidebar. */
export function resolveSubuserUnauthorizedRedirect(
  routeName: string | null | undefined,
  hasAnySubuserPermission: (permissions: string[]) => boolean,
  navItems: NavItem[] = [],
) {
  if (routeName) {
    const sibling = getFirstAccessibleSiblingRoute(routeName, hasAnySubuserPermission, navItems)
    if (sibling) return sibling
  }

  return getFirstAccessibleSubuserRoute(hasAnySubuserPermission, navItems)
    ?? { name: 'unauthorized' }
}
